import { useState, useEffect, useRef } from "react";
import { OwnThisCardButton } from "./card-purchase";
import { LifetimeVisitorCount } from "./live-visitor";
import ClaritySessionBooking from "./clarity-session-booking";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Key, ArrowRight, ArrowLeft, Youtube, Facebook, Mail, Sparkles, X, Play, Pause, Volume2, Heart } from "lucide-react";
import axios from "axios";
import {
  Nav, Footer, Divider, CheckoutModal, Toast, SEO, BuyButton, FridayMirror, fireLeadEvent, fireViewContent, API,
} from "./components";
import {
  LOGO_URL, DAWN_PORTRAIT, DECK_IMG, HERO_PHOTO_URL, HERO_PHOTO_POSITION,
  PRODUCTS, DECK, SESSIONS, SHORTS,
  HOME_SEO, ABOUT_SEO, FOCUS_MODE, FOCUS_SESSION_PATH,
} from "./data";
import { CARDS } from "./cards";

function CardGallery() {
  const [pulled, setPulled] = useState(null);
  const [pulling, setPulling] = useState(false);
  const [selected, setSelected] = useState(null);

  const pullCard = () => {
    setPulling(true);
    setPulled(null);
    setTimeout(() => {
      const random = CARDS[Math.floor(Math.random() * CARDS.length)];
      setPulled(random);
      setPulling(false);
      if (typeof window.gtag === "function") {
        window.gtag("event", "pull_card", { card_name: random.name });
      }
    }, 900);
  };

  return (
    <section id="deck-gallery" data-testid="section-deck-gallery" className="relative py-24 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        {/* Pull a Card */}
        <div className="text-center mb-20 relative">
          <div className="aura" style={{ width: 500, height: 500, top: "-100px", left: "50%", transform: "translateX(-50%)", opacity: 0.4 }}></div>
          <div className="relative z-10">
            <div className="section-label" data-testid="pull-label">Free Daily Reflection</div>
            <h2 className="display-serif text-4xl sm:text-5xl md:text-6xl mb-4" data-testid="pull-heading">
              Pull a <em>Mirror Lotus</em> card.
            </h2>
            <p className="text-[#F2EFE8]/60 text-lg mb-10 max-w-xl mx-auto">
              Ask a question in your heart. Then let the mirror answer.
            </p>
            <button onClick={pullCard} disabled={pulling} className="btn-gold" data-testid="pull-button">
              {pulling ? "Shuffling..." : (pulled ? "Pull Another" : "Pull My Card")}
            </button>
            {pulled && (
              <div className="mt-12 flex flex-col md:flex-row items-center gap-10 max-w-4xl mx-auto animate-fade-in text-left" data-testid="pulled-card">
                <div className="pulled-glow flex-shrink-0">
                  <img src={pulled.url} alt={pulled.name} className="w-64 sm:w-72 rounded-lg shadow-2xl float" style={{ boxShadow: "0 20px 60px rgba(245, 212, 138, 0.4)" }} />
                  <span className="ruby-main">✦</span>
                </div>
                <div className="flex-1 sf-card rounded-sm p-8 jewel-card jewel-gold">
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#B58B2E] mb-2">Your Mirror Today</div>
                  <p className="serif text-4xl gold-shimmer mb-6" data-testid="pulled-card-name">{pulled.name}</p>
                  <p className="serif italic text-xl text-[#F5D48A] mb-6 leading-snug" data-testid="pulled-card-essence">
                    "{pulled.essence}"
                  </p>
                  <div className="border-l-2 border-[#B58B2E]/60 pl-4">
                    <div className="text-[10px] tracking-[0.25em] uppercase text-[#A89FA8] mb-2">Reflection</div>
                    <p className="text-[#F2EFE8]/85 text-base leading-relaxed" data-testid="pulled-card-prompt">
                      {pulled.prompt}
                    </p>
                  </div>
                  <div className="mt-6 pt-5 border-t border-[#B58B2E]/25">
                    <OwnThisCardButton card={pulled} className="w-full sm:w-auto" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Full Gallery */}
        <div className="text-center mb-14">
          <div className="section-label">The Full Deck</div>
          <h3 className="display-serif text-3xl sm:text-4xl mb-4">All <em>21 sacred cards.</em></h3>
          <p className="text-[#F2EFE8]/60">Hover any card to reveal its name.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
          {CARDS.map((c, i) => (
            <button
              key={i}
              onClick={() => setSelected(c)}
              data-testid={`deck-card-${i}`}
              className="mirror-card group relative rounded-lg overflow-hidden border border-[#B58B2E]/25 hover:border-[#F5D48A] transition-all duration-500 hover:scale-[1.04]"
              style={{ boxShadow: "0 6px 24px rgba(0,0,0,0.4)", animationDelay: `${i * 0.15}s` }}
            >
              <img src={c.url} alt={c.name} className="w-full h-auto block" loading="lazy" />
              <span className="ruby">✦</span>
              <span className="ruby-side left">✦</span>
              <span className="ruby-side right">✦</span>
              <span className="spark">✦</span>
              <span className="spark">✧</span>
              <span className="spark">✦</span>
              <span className="spark">✧</span>
              <span className="spark">✦</span>
              <span className="spark">✧</span>
              <span className="spark">✦</span>
              <span className="spark">✧</span>
              <span className="shimmer"></span>
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A1140] via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end justify-center pb-4 z-10">
                <span className="serif text-lg text-[#F5D48A]">{c.name}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Card Zoom Modal */}
      {selected && (
        <div className="modal-backdrop" onClick={() => setSelected(null)} data-testid="card-zoom-modal">
          <div className="relative max-w-3xl w-full flex flex-col md:flex-row gap-8 items-center" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} data-testid="card-zoom-close" className="absolute -top-12 right-0 text-[#F5D48A] hover:text-white p-2 z-10">
              <X size={26} />
            </button>
            <div className="pulled-glow flex-shrink-0">
              <img src={selected.url} alt={selected.name} className="w-64 sm:w-72 rounded-lg" style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.7)" }} />
              <span className="ruby-main">✦</span>
            </div>
            <div className="flex-1 text-left">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#B58B2E] mb-2">Mirror Lotus</div>
              <p className="serif text-4xl gold-shimmer mb-5">{selected.name}</p>
              <p className="serif italic text-xl text-[#F5D48A] mb-5 leading-snug">"{selected.essence}"</p>
              <div className="border-l-2 border-[#B58B2E]/60 pl-4">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A89FA8] mb-2">Reflection</div>
                <p className="text-[#F2EFE8]/85 text-base leading-relaxed">{selected.prompt}</p>
              </div>
              <div className="mt-6 pt-5 border-t border-[#B58B2E]/25">
                <OwnThisCardButton card={selected} />
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ============ Shared: Hero ============
// ============ Voice Welcome (Dawn's homepage audio greeting) ============
function VoiceWelcome() {
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = new Audio("/audio/dawn-welcome.m4a");
    audio.preload = "metadata";
    audioRef.current = audio;
    const onEnd = () => setPlaying(false);
    audio.addEventListener("ended", onEnd);
    return () => {
      audio.pause();
      audio.removeEventListener("ended", onEnd);
    };
  }, []);

  const toggle = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.currentTime = 0;
      audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
    }
  };

  return (
    <div className="flex justify-center mb-8">
      <button
        onClick={toggle}
        className="voice-welcome-btn group inline-flex items-center gap-3 px-6 py-3 rounded-full border border-[#B58B2E]/50 bg-[#2A1140]/60 hover:bg-[#3D1A5A]/80 hover:border-[#F5D48A] backdrop-blur-sm transition"
        aria-label={playing ? "Pause Dawn's welcome" : "Play Dawn's welcome"}
        data-testid="voice-welcome-btn"
      >
        <span className={`voice-welcome-icon relative flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br from-[#F5D48A] to-[#B58B2E] text-[#2A1140] ${playing ? 'is-playing' : ''}`}>
          {playing ? <Pause size={16} strokeWidth={2.5} /> : <Play size={16} strokeWidth={2.5} className="translate-x-[1px]" />}
        </span>
        <span className="text-left">
          <span className="block text-[10px] tracking-[0.3em] uppercase text-[#B58B2E]">A word from Dawn</span>
          <span className="serif text-[#F5D48A] text-base leading-tight">Hear Dawn welcome you</span>
        </span>
        <Volume2 size={16} className="text-[#F5D48A]/70 ml-1" />
      </button>
    </div>
  );
}

function Hero() {
  return (
    <section id="hero" data-testid="section-hero" className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-16 overflow-hidden">
      <div className="sf-ambient"></div>
      <div className="aura" style={{ width: 720, height: 720, top: "10%", left: "50%", transform: "translateX(-50%)", opacity: 0.55 }}></div>
      <div className="relative z-10 max-w-7xl mx-auto text-center px-6">
        <div className="grid grid-cols-2 gap-3 sm:gap-6 w-full max-w-[37rem] md:max-w-[48rem] lg:max-w-[59rem] xl:max-w-[65rem] mx-auto mt-6 mb-8" data-testid="hero-logo-photo-pair">
          <div className="hero-frame float">
            <img src={LOGO_URL} alt="SnapshotFame Life Clarity Chic" className="w-full h-full object-cover" data-testid="hero-logo" />
          </div>
          <div className="hero-frame float" style={{ animationDelay: "0.6s" }}>
            <img src={HERO_PHOTO_URL} alt="Dawn Delisle" className="w-full h-full object-contain" style={{ objectPosition: HERO_PHOTO_POSITION }} data-testid="hero-photo" />
          </div>
        </div>
        <div className="flex flex-col items-center justify-center mb-12 px-4" data-testid="hero-session-cta">
          <p className="serif italic text-2xl sm:text-3xl text-[#F2EFE8] mb-5 leading-snug" data-testid="hero-session-headline">
            Need someone to really listen? <span className="whitespace-nowrap">Let&apos;s talk.</span>
          </p>
          <Link to={FOCUS_MODE ? FOCUS_SESSION_PATH : "/#sessions"} className="btn-gold text-lg sm:text-xl px-12 sm:px-16 py-5 inline-flex items-center gap-2" data-testid="hero-cta-sessions">
            Book a Private Session ⭐
          </Link>
          <p className="text-lg sm:text-xl text-[#F2EFE8]/85 max-w-xl mt-5 leading-relaxed" data-testid="hero-session-subtext">
            A private, judgment-free conversation. Real connection, fresh perspectives, and someone who genuinely listens.
          </p>
        </div>
        {/* Testimonial */}
        <div className="max-w-2xl mx-auto mb-8 sf-card jewel-card jewel-gold rounded-sm p-8 text-left" data-testid="testimonial-jenny">
          <div className="flex items-center justify-center gap-1 mb-4 text-[#F5D48A] text-lg">
            <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
          </div>
          <p className="text-[#F2EFE8]/90 leading-relaxed mb-6 italic">
            &ldquo;Thank you Dawn, our conversation helped me so much! I finally got the clarity to make a move… you&rsquo;re always there for me in those cloudy days. Thank you!!!&rdquo;
          </p>
          <div className="text-center border-t border-[#B58B2E]/25 pt-4">
            <p className="serif text-lg text-[#F5D48A]">— Jenny</p>
            <Heart size={16} className="mx-auto mt-2 text-[#EC4899]" fill="#EC4899" strokeWidth={1.5} data-testid="testimonial-jenny-heart" />
          </div>
        </div>
        <div className="max-w-2xl mx-auto mb-8 sf-card jewel-card jewel-gold rounded-sm p-8 text-left" data-testid="testimonial-mike">
          <div className="flex items-center justify-center gap-1 mb-4 text-[#F5D48A] text-lg">
            <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
          </div>
          <p className="text-[#F2EFE8]/90 leading-relaxed mb-6 italic">
            &ldquo;Dawn has helped guide me through a difficult time in my life. She has a way of making serious situations feel less overwhelming and helping me see things from a different perspective. She&rsquo;s always there to listen, and I genuinely enjoy our conversations. Sometimes you just need someone like Dawn to talk to.&rdquo;
          </p>
          <div className="text-center border-t border-[#B58B2E]/25 pt-4">
            <p className="serif text-lg text-[#F5D48A]">— Mike</p>
          </div>
        </div>
        <div className="max-w-2xl mx-auto sf-card jewel-card jewel-gold rounded-sm p-8 text-left" data-testid="testimonial-israel">          <div className="flex items-center justify-center gap-1 mb-4 text-[#F5D48A] text-lg">
            <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
          </div>
          <p className="text-[#F2EFE8]/90 leading-relaxed mb-4 italic">
            &quot;I had a business situation that was causing me a lot of stress. Dawn asked if I was open to a different perspective, and the way she listened and asked questions made me feel comfortable instead of judged.
          </p>
          <p className="text-[#F2EFE8]/90 leading-relaxed mb-4 italic">
            During our conversation, I could actually feel a shift. I saw my situation in a completely different way and walked away feeling lighter, calmer, and much clearer about my next steps.
          </p>
          <p className="text-[#F2EFE8]/90 leading-relaxed mb-6 italic">
            It wasn&apos;t just her advice — it was the way she guided the conversation that made all the difference.&quot;
          </p>
          <div className="text-center border-t border-[#B58B2E]/25 pt-4">
            <p className="serif text-lg text-[#F5D48A]">Israel Hernandez</p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#B58B2E] mt-1">Client</p>
          </div>
        </div>
      </div>
    </section>
  );
}

function AboutTeaser() {
  return (
    <section id="about" data-testid="section-about" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative">
          <div className="aura" style={{ width: 380, height: 380, top: "-40px", left: "-40px", opacity: 0.4 }}></div>
          <div className="relative rounded-sm overflow-hidden border border-[#B58B2E]/30 shadow-2xl">
            <img src={DAWN_PORTRAIT} alt="Dawn Delisle, Life Clarity Chic" className="w-full h-auto object-contain" data-testid="about-portrait" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A1140] via-transparent to-transparent"></div>
          </div>
          <div className="absolute -bottom-6 -right-6 hidden md:block">
            <img src={LOGO_URL} alt="" className="w-24 opacity-90" />
          </div>
        </div>
        <div>
          <div className="section-label" data-testid="about-label">Meet Dawn</div>
          <h2 className="display-serif text-4xl sm:text-5xl mb-8" data-testid="about-heading">
            Come sit in <em>my chair.</em> 💋
          </h2>
          <p className="text-[#F2EFE8]/85 text-lg leading-relaxed mb-5" data-testid="about-p1">
            For over 40 years, my chair has been a place for fabulous stories, heartfelt conversations, a little laughter, and the occasional juicy secret.
          </p>
          <p className="text-[#F2EFE8]/80 text-lg leading-relaxed mb-5" data-testid="about-p2">
            I&apos;ve learned that sometimes all we really need is someone who listens, understands, and isn&apos;t afraid to share a fresh perspective.
          </p>
          <p className="text-[#F2EFE8]/80 text-lg leading-relaxed mb-5" data-testid="about-p3">
            So bring your stories, your questions, your laughter, or whatever&apos;s on your mind.
          </p>
          <p className="text-[#F2EFE8]/85 text-lg leading-relaxed mb-5" data-testid="about-p4">
            No judgment, no pressure. Just you, me, and a really good conversation.
          </p>
          <p className="serif italic text-2xl text-[#F5D48A] leading-snug mb-8" data-testid="about-p5">
            Who knows? We might even have a little fun along the way. 😉
          </p>
          <VoiceWelcome />
          <Link to="/about" className="btn-outline-gold inline-flex items-center gap-2" data-testid="about-read-more">
            Read Dawn's Story <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ProductCard({ p }) {
  return (
    <Link
      to={`/snapshots/${p.slug}`}
      data-testid={`product-card-${p.id}`}
      className={`sf-card jewel-card jewel-${p.jewel} rounded-sm p-8 flex flex-col group`}
    >
      <div className="flex items-center gap-3 mb-4">
        <span className="jewel-dot"></span>
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8]">{p.tag}</span>
      </div>
      <h3 className="serif text-3xl text-[#F2EFE8] mb-3 leading-tight" data-testid={`product-name-${p.id}`}>{p.name}</h3>
      <p className="serif italic text-lg mb-4" style={{ color: p.theme }}>{p.tagline}</p>
      <p className="text-[#F2EFE8]/60 text-sm leading-relaxed mb-8 flex-grow">{p.desc}</p>
      <div className="flex items-baseline gap-2 mb-6">
        <span className="text-xs text-[#A89FA8]">USD</span>
        <span className="serif text-4xl gold-shimmer" data-testid={`product-price-${p.id}`}>${p.price}</span>
      </div>
      <div className="flex items-center gap-2 text-[#F5D48A] text-sm uppercase tracking-widest group-hover:gap-4 transition-all">
        Explore <ArrowRight size={16} />
      </div>
    </Link>
  );
}

function SnapshotsSection() {
  return (
    <section id="snapshots" data-testid="section-snapshots" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="section-label" data-testid="snapshots-label">Digital Snapshots</div>
          <h2 className="display-serif text-4xl sm:text-5xl md:text-6xl mb-6" data-testid="snapshots-heading">
            Guided <em>self-reflection</em> experiences
          </h2>
          <p className="text-[#F2EFE8]/60 max-w-2xl mx-auto text-lg">
            Each Snapshot pairs symbolic prompts, selfie mirrors, and guided journaling — so you don't get told who you are. You see it.
          </p>
        </div>
        <Divider />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          {PRODUCTS.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </div>
    </section>
  );
}

function CardsTeaser() {
  return (
    <section id="cards" data-testid="section-cards" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="relative order-2 md:order-1">
          <div className="aura" style={{ width: 500, height: 500, top: "-30px", right: "-30px", opacity: 0.5 }}></div>
          <div className="relative flex justify-center items-center h-[520px]">
            <img src={CARDS[4].url} alt="Truth" className="absolute w-44 sm:w-48 rounded-lg shadow-2xl" style={{ transform: "rotate(-22deg) translate(-140px, 20px)", zIndex: 1, boxShadow: "0 20px 50px rgba(0,0,0,0.7)" }} />
            <img src={CARDS[13].url} alt="Love" className="absolute w-44 sm:w-48 rounded-lg shadow-2xl" style={{ transform: "rotate(22deg) translate(140px, 20px)", zIndex: 1, boxShadow: "0 20px 50px rgba(0,0,0,0.7)" }} />
            <img src={CARDS[8].url} alt="The Key" className="relative w-52 sm:w-56 rounded-lg shadow-2xl float" style={{ zIndex: 2, boxShadow: "0 30px 60px rgba(245, 212, 138, 0.5)" }} data-testid="cards-image" />
          </div>
        </div>
        <div className="order-1 md:order-2">
          <div className="section-label" data-testid="cards-label">Mirror Lotus</div>
          <h2 className="display-serif text-4xl sm:text-5xl mb-6" data-testid="cards-heading">
            A 21-card <em>custom deck</em> for the seeker.
          </h2>
          <p className="text-[#F2EFE8]/70 text-lg leading-relaxed mb-6">
            The Mirror Lotus System — a hand-crafted digital deck of symbolic cards that pair with every Snapshot to reveal the patterns behind what you see.
          </p>
          <div className="flex items-baseline gap-2 mb-6">
            <span className="text-xs text-[#A89FA8]">USD</span>
            <span className="serif text-5xl gold-shimmer" data-testid="cards-price">$75</span>
          </div>
          <Link to="/mirror-lotus" className="btn-gold inline-flex items-center gap-2" data-testid="cards-explore">
            Explore Mirror Lotus <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function SessionCard({ s }) {
  return (
    <Link to={`/sessions/${s.slug}`} data-testid={`session-card-${s.id}`} className={`sf-card jewel-card jewel-${s.jewel} rounded-sm p-10 flex flex-col group`}>
      <div className="flex items-center gap-3 mb-6">
        <Sparkles size={16} className="text-[#F5D48A]" />
        <span className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8]">{s.duration}</span>
      </div>
      <h3 className="serif text-4xl text-[#F2EFE8] mb-4 leading-tight" data-testid={`session-name-${s.id}`}>{s.shortName}</h3>
      <p className="text-[#F2EFE8]/65 text-base leading-relaxed mb-10 flex-grow">{s.desc}</p>
      <div className="flex items-baseline gap-2 mb-6">
        {s.perMinute ? (
          <span className="serif text-5xl gold-shimmer" data-testid={`session-price-${s.id}`}>{s.priceLabel}</span>
        ) : (
          <>
            <span className="text-xs text-[#A89FA8]">USD</span>
            <span className="serif text-5xl gold-shimmer" data-testid={`session-price-${s.id}`}>${s.price}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 text-[#F5D48A] text-sm uppercase tracking-widest group-hover:gap-4 transition-all">
        {s.ctaLabel || "Learn More"} <ArrowRight size={16} />
      </div>
    </Link>
  );
}

function SessionsSection() {
  return (
    <section id="sessions" data-testid="section-sessions" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-16">
          <div className="section-label" data-testid="sessions-label">Private Sessions</div>
          <h2 className="display-serif text-4xl sm:text-5xl md:text-6xl mb-6" data-testid="sessions-heading">
            Sit with <em>Dawn.</em>
          </h2>
          <p className="text-[#F2EFE8]/60 max-w-2xl mx-auto text-lg">
            In a private session, Dawn shows you what's actually driving your results — clearly. You're not stuck. You're repeating patterns.
          </p>
        </div>
        <Divider />
        <div className={`grid gap-8 mx-auto ${FOCUS_MODE ? "max-w-xl" : "md:grid-cols-2 max-w-4xl"}`}>
          {SESSIONS.filter((s) => !FOCUS_MODE || s.perMinute).map((s) => <SessionCard key={s.id} s={s} />)}
        </div>
      </div>
    </section>
  );
}

function ShortsSection() {
  return (
    <section id="watch" data-testid="section-watch" className="relative py-24 lg:py-32 px-6 lg:px-12 bg-[#2A1140]/60">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="section-label" data-testid="watch-label">Insights</div>
          <h2 className="display-serif text-4xl sm:text-5xl mb-4" data-testid="watch-heading">
            From <em>@dawndelisleslife</em>
          </h2>
          <p className="text-[#F2EFE8]/60">Short reflections. Real patterns.</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          {SHORTS.map((s) => (
            <a key={s.id} href={`https://www.youtube.com/shorts/${s.id}`} target="_blank" rel="noopener noreferrer"
              data-testid={`short-${s.id}`}
              className="sf-card jewel-card jewel-gold rounded-sm overflow-hidden aspect-[9/16] block group relative">
              <img src={`https://i.ytimg.com/vi/${s.id}/hqdefault.jpg`} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A1140] via-transparent to-transparent"></div>
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="serif text-sm text-[#F5D48A] leading-tight">{s.title}</div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                <Youtube size={48} className="text-[#F5D48A] drop-shadow-2xl" />
              </div>
            </a>
          ))}
        </div>
        <div className="text-center mt-10">
          <a href="https://www.youtube.com/@dawndelisleslife" target="_blank" rel="noopener noreferrer" className="btn-outline-gold" data-testid="watch-more-cta">Watch More on YouTube</a>
        </div>
      </div>
    </section>
  );
}

function ConnectSection() {
  return (
    <section id="connect" data-testid="section-connect" className="relative py-24 lg:py-32 px-6 lg:px-12">
      <div className="max-w-4xl mx-auto text-center">
        <div className="section-label" data-testid="connect-label">Connect</div>
        <h2 className="display-serif text-5xl sm:text-6xl md:text-7xl mb-8" data-testid="connect-heading">
          Come sit in <em>my chair.</em>
        </h2>
        <p className="text-[#F2EFE8]/70 text-lg mb-12 max-w-xl mx-auto">
          Have a question? Curious about a session? Reach out — I read every message personally.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
          <a href="mailto:divainkhair@gmail.com" className="btn-gold flex items-center gap-3" data-testid="connect-email">
            <Mail size={16} /> divainkhair@gmail.com
          </a>
        </div>
        <div className="flex items-center justify-center gap-6">
          <a href="https://www.facebook.com/dawndelislepsychic/" target="_blank" rel="noopener noreferrer" data-testid="connect-facebook-page" aria-label="Facebook Page" className="w-12 h-12 rounded-full border border-[#B58B2E]/40 flex items-center justify-center text-[#B58B2E] hover:text-[#F5D48A] hover:border-[#F5D48A] transition-colors"><Facebook size={18} /></a>
          <a href="https://www.facebook.com/dawn.delisle/" target="_blank" rel="noopener noreferrer" data-testid="connect-facebook-personal" aria-label="Facebook Personal" className="w-12 h-12 rounded-full border border-[#B58B2E]/40 flex items-center justify-center text-[#B58B2E] hover:text-[#F5D48A] hover:border-[#F5D48A] transition-colors"><Facebook size={18} /></a>
          <a href="https://www.youtube.com/@dawndelisleslife" target="_blank" rel="noopener noreferrer" data-testid="connect-youtube" aria-label="YouTube" className="w-12 h-12 rounded-full border border-[#B58B2E]/40 flex items-center justify-center text-[#B58B2E] hover:text-[#F5D48A] hover:border-[#F5D48A] transition-colors"><Youtube size={18} /></a>
        </div>
      </div>
    </section>
  );
}

// ============ Handle scroll to hash on load ============
function useScrollToHash() {
  useEffect(() => {
    if (window.location.hash) {
      setTimeout(() => {
        const el = document.querySelector(window.location.hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, []);
}

// ============ HOME PAGE ============
// ============ FRONT DOOR — Free 15-Min Clarity Call ============
function FrontDoor() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <section id="clarity-call" data-testid="section-front-door" className="relative py-20 lg:py-24 px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <div className="section-label mb-4" data-testid="front-door-label">A Complimentary Invitation</div>
          <h2 className="display-serif text-3xl sm:text-4xl md:text-5xl mb-6" data-testid="front-door-heading">
            Curious about <em>Snapshot Fame?</em>
          </h2>
          <p className="text-[#F2EFE8]/85 text-lg sm:text-xl leading-relaxed max-w-xl mx-auto mb-10" data-testid="front-door-body">
            Let&apos;s spend 15 minutes together and see if it&apos;s right for you.
          </p>
          <button
            type="button"
            onClick={() => { setOpen(true); fireLeadEvent && fireLeadEvent("front-door-cta"); }}
            className="btn-gold text-lg px-10 py-4 inline-flex items-center gap-2"
            data-testid="front-door-cta"
          >
            <span>Book My Free Call</span>
            <ArrowRight size={18} />
          </button>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8] mt-6">
            Answered personally by Dawn · Usually within 24 hours
          </p>
        </div>
      </section>
      <ClarityCallModal open={open} onClose={() => setOpen(false)} source="homepage-front-door" />
    </>
  );
}

// ============ Clarity Call Modal (booking form) ============
function ClarityCallModal({ open, onClose, source = "homepage-front-door" }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      setError("Please share your name, email, and best phone number.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/clarity-calls`, { ...form, source });
      setDone(true);
      fireLeadEvent && fireLeadEvent("clarity-call-submitted");
    } catch (err) {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4 py-8 bg-black/60 backdrop-blur-sm"
      data-testid="clarity-call-modal"
      onClick={onClose}
    >
      <div
        className="relative max-w-md w-full bg-[#2A1140] border border-[#B58B2E]/50 rounded-sm p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 text-[#F5D48A]/70 hover:text-[#F5D48A]"
          data-testid="clarity-call-close"
        >
          <X size={22} />
        </button>
        {!done ? (
          <>
            <div className="text-center mb-6">
              <div className="section-label mb-2">Complimentary Call</div>
              <h3 className="display-serif text-2xl text-[#F5D48A]">Let&apos;s talk.</h3>
              <p className="text-[#F2EFE8]/70 text-sm mt-2">15 minutes. Free. Just a conversation.</p>
            </div>
            <form onSubmit={submit} noValidate className="space-y-3" data-testid="clarity-call-form">
              <input
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A]"
                data-testid="clarity-call-name"
                required
              />
              <input
                type="email"
                placeholder="Email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A]"
                data-testid="clarity-call-email"
                required
              />
              <input
                type="tel"
                placeholder="Best phone number (WhatsApp works)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A]"
                data-testid="clarity-call-phone"
                required
              />
              <textarea
                placeholder="What's on your mind? (optional)"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="w-full px-4 py-3 bg-black/30 border border-[#B58B2E]/40 rounded-sm text-[#F2EFE8] focus:outline-none focus:border-[#F5D48A] resize-none"
                rows="3"
                data-testid="clarity-call-message"
              />
              {error && <p className="text-[#F0847C] text-sm" data-testid="clarity-call-error">{error}</p>}
              <button
                type="submit"
                disabled={submitting}
                className="btn-gold w-full mt-2 disabled:opacity-60"
                data-testid="clarity-call-submit"
              >
                {submitting ? "Sending..." : "Send Request"}
              </button>
              <p className="text-[10px] text-[#A89FA8] text-center mt-3">
                Your details go straight to Dawn. Nothing is shared with anyone.
              </p>
            </form>
          </>
        ) : (
          <div className="text-center py-6" data-testid="clarity-call-success">
            <div className="text-4xl mb-3">🌸</div>
            <h3 className="display-serif text-2xl text-[#F5D48A] mb-3">Wonderful.</h3>
            <p className="text-[#F2EFE8]/80 leading-relaxed">
              Dawn will reach out to you personally within 24 hours to arrange your call.
            </p>
            <button
              onClick={() => { setDone(false); setForm({ name: "", email: "", phone: "", message: "" }); onClose(); }}
              className="btn-outline-gold mt-6"
              data-testid="clarity-call-done"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ Dismissible Exit Banner ============
function ExitBanner() {
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("sf_exit_banner_dismissed") === "1") return;
    const onScroll = () => {
      const scrolled = window.scrollY + window.innerHeight;
      const height = document.body.scrollHeight;
      if (scrolled / height > 0.55) {
        setVisible(true);
        window.removeEventListener("scroll", onScroll);
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dismiss = () => {
    setDismissed(true);
    sessionStorage.setItem("sf_exit_banner_dismissed", "1");
  };

  if (!visible || dismissed) return null;

  return (
    <>
      <div
        className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[100] max-w-2xl w-[92%] bg-[#2A1140]/98 border border-[#B58B2E]/50 rounded-sm p-4 sm:p-5 shadow-2xl backdrop-blur-sm animate-fade-in"
        data-testid="exit-banner"
      >
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <p className="serif text-[#F5D48A] text-base sm:text-lg leading-snug mb-1" data-testid="exit-banner-text">
              Not sure yet? Book a complimentary 15-minute conversation.
            </p>
            <p className="text-[#F2EFE8]/70 text-xs sm:text-sm">
              Sometimes a simple conversation is all it takes to know your next step.
            </p>
          </div>
          <button
            onClick={() => { setOpen(true); fireLeadEvent && fireLeadEvent("exit-banner-cta"); }}
            className="btn-gold text-xs sm:text-sm whitespace-nowrap px-4 py-2"
            data-testid="exit-banner-cta"
          >
            Book Free Call
          </button>
          <button
            onClick={dismiss}
            aria-label="Dismiss"
            className="text-[#F5D48A]/60 hover:text-[#F5D48A] p-1"
            data-testid="exit-banner-dismiss"
          >
            <X size={18} />
          </button>
        </div>
      </div>
      <ClarityCallModal open={open} onClose={() => setOpen(false)} source="exit-banner" />
    </>
  );
}

export function HomePage() {
  useScrollToHash();
  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "ProfessionalService",
    name: "SnapshotFame — Life Clarity Chic",
    description: HOME_SEO.description,
    provider: { "@type": "Person", name: "Dawn Delisle", jobTitle: "Life Clarity Chic" },
    url: "https://snapshotfame.com/",
  };
  return (
    <>
      <SEO {...HOME_SEO} canonical="https://snapshotfame.com/" jsonLd={homeJsonLd} />
      <Hero />
      {!FOCUS_MODE && <FrontDoor />}
      {!FOCUS_MODE && <LifetimeVisitorCount />}
      <AboutTeaser />
      {!FOCUS_MODE && <SnapshotsSection />}
      {!FOCUS_MODE && <CardsTeaser />}
      <SessionsSection />
      {!FOCUS_MODE && <FridayMirror />}
      {!FOCUS_MODE && <ShortsSection />}
      <ConnectSection />
      {!FOCUS_MODE && <ExitBanner />}
    </>
  );
}

// ============ PRODUCT DETAIL PAGE ============
export function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [toast, setToast] = useState("");
  const product = PRODUCTS.find((p) => p.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (product) fireViewContent(product);
  }, [slug, product]);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="product-not-found">
        <h1 className="serif text-4xl text-[#F5D48A] mb-4">Not found</h1>
        <button onClick={() => navigate("/")} className="btn-gold">Return Home</button>
      </div>
    );
  }

  const handleSubmit = async (p, form) => {
    try {
      await axios.post(`${API}/orders`, {
        product_id: p.id, product_name: p.name, price: p.price,
        customer_name: form.name, customer_email: form.email,
      });
      fireLeadEvent(p);
      setCheckoutProduct(null);
      setToast(`Thank you, ${form.name.split(" ")[0]}! Dawn will reach out soon.`);
    } catch (e) {
      setToast("Something went wrong. Please email divainkhair@gmail.com directly.");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: product.seo.description,
    brand: { "@type": "Brand", name: "SnapshotFame" },
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: "USD",
      availability: "https://schema.org/InStock",
    },
  };

  return (
    <>
      <SEO {...product.seo} canonical={`https://snapshotfame.com/snapshots/${product.slug}`} jsonLd={jsonLd} />
      <section className="pt-32 pb-16 px-6 lg:px-12" data-testid={`page-product-${product.id}`}>
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate("/#snapshots")} className="flex items-center gap-2 text-[#A89FA8] hover:text-[#F5D48A] text-xs tracking-widest uppercase mb-10" data-testid="back-to-snapshots">
            <ArrowLeft size={14} /> All Snapshots
          </button>
          <div className="flex items-center gap-3 mb-6">
            <span className="jewel-dot" style={{ background: product.theme, boxShadow: `0 0 16px ${product.theme}` }}></span>
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8]" data-testid="detail-tag">{product.tag}</span>
          </div>
          <h1 className="display-serif text-5xl sm:text-6xl md:text-7xl mb-6" data-testid="detail-h1">{product.seo.h1}</h1>
          <p className="serif italic text-2xl mb-10" style={{ color: product.theme }} data-testid="detail-subheading">
            {product.seo.subheading}
          </p>

          <div className="grid md:grid-cols-3 gap-12 mt-16">
            <div className="md:col-span-2 space-y-6">
              {product.longDesc.map((p, i) => (
                <p key={i} className="text-[#F2EFE8]/80 text-lg leading-relaxed" data-testid={`detail-p-${i}`}>{p}</p>
              ))}
              <div className="pt-6 border-t border-[#B58B2E]/20 mt-10">
                <div className="section-label mb-4">What's Included</div>
                <ul className="space-y-3">
                  {product.includes.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#F2EFE8]/75" data-testid={`detail-include-${i}`}>
                      <Key size={16} className="text-[#B58B2E] mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div>
              <div className="sf-card jewel-card rounded-sm p-8 sticky top-28" style={{ ["--jewel"]: product.theme }}>
                <div className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8] mb-2">Digital Download</div>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-xs text-[#A89FA8]">USD</span>
                  <span className="serif text-5xl gold-shimmer" data-testid="detail-price">${product.price}</span>
                </div>
                <BuyButton product={product} onBuy={setCheckoutProduct} />
                <p className="text-[10px] text-center text-[#A89FA8] mt-4 tracking-widest uppercase">Instant Download</p>
                {(product.slug === "financial-freedom" || product.slug === "love-connection" || product.slug === "six-color-complete" || product.slug === "dream-connection") && (
                  <div className="mt-6 pt-5 border-t border-[#B58B2E]/25 text-center">
                    <p className="text-[10px] text-[#A89FA8] tracking-widest uppercase mb-2">Already Purchased?</p>
                    <Link
                      to={`/experience/${product.slug}`}
                      data-testid={`enter-experience-${product.slug}`}
                      className="text-[#F5D48A] hover:text-[#FFE9BF] text-xs tracking-widest uppercase inline-flex items-center gap-2 underline underline-offset-4"
                    >
                      Enter Your Access Code <ArrowRight size={12} />
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <FridayMirror />
      <CheckoutModal product={checkoutProduct} onClose={() => setCheckoutProduct(null)} onSubmit={handleSubmit} />
      <Toast msg={toast} onClose={() => setToast("")} />
    </>
  );
}

// ============ MIRROR LOTUS PAGE ============
// ============ Guidebook Preview strip on Mirror Lotus product page ============
function GuidebookPreview() {
  const previewCards = [CARDS[10], CARDS[14], CARDS[16]]; // Creativity, Liberation, Strength
  return (
    <section className="relative py-20 lg:py-24 px-6 lg:px-12" data-testid="guidebook-preview">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <div className="section-label" data-testid="guidebook-label">A Taste of the Guidebook</div>
          <h2 className="display-serif text-3xl sm:text-4xl md:text-5xl mb-3" data-testid="guidebook-heading">
            Three cards. <em>Three mirrors.</em>
          </h2>
          <p className="text-[#F2EFE8]/70 max-w-2xl mx-auto text-base sm:text-lg">
            Every card in the deck opens like this — an essence to sit with, and a question to carry.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-8 lg:gap-10">
          {previewCards.map((c, i) => (
            <div
              key={i}
              className="sf-card jewel-card jewel-gold rounded-sm p-6 flex flex-col items-center text-center"
              data-testid={`guidebook-card-${i}`}
              style={{ animationDelay: `${i * 0.1}s` }}
            >
              <div className="pulled-glow mb-6">
                <img
                  src={c.url}
                  alt={c.name}
                  className="w-44 sm:w-48 rounded-lg"
                  style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.55)" }}
                />
              </div>
              <p className="serif text-2xl gold-shimmer mb-4" data-testid={`guidebook-card-name-${i}`}>{c.name}</p>
              <p className="serif italic text-[#F5D48A] text-base leading-snug mb-5" data-testid={`guidebook-card-essence-${i}`}>
                &ldquo;{c.essence}&rdquo;
              </p>
              <div className="w-full border-l-2 border-[#B58B2E]/60 pl-4 text-left">
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#A89FA8] mb-2">Reflection</div>
                <p className="text-[#F2EFE8]/80 text-sm leading-relaxed" data-testid={`guidebook-card-prompt-${i}`}>
                  {c.prompt}
                </p>
              </div>
              <div className="mt-5 w-full">
                <OwnThisCardButton card={c} variant="outline" className="w-full" />
              </div>
            </div>
          ))}
        </div>
        <p className="text-center text-[#A89FA8] text-xs tracking-[0.3em] uppercase mt-10">
          21 sacred cards · full guidebook included
        </p>
      </div>
    </section>
  );
}

// ============ Mirror Lotus PRODUCT PAGE ============
export function MirrorLotusPage() {
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [toast, setToast] = useState("");
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (p, form) => {
    try {
      await axios.post(`${API}/orders`, {
        product_id: p.id, product_name: p.name, price: p.price,
        customer_name: form.name, customer_email: form.email,
      });
      fireLeadEvent(p);
      setCheckoutProduct(null);
      setToast(`Thank you, ${form.name.split(" ")[0]}! Dawn will reach out soon.`);
    } catch (e) {
      setToast("Something went wrong. Please email divainkhair@gmail.com directly.");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: DECK.name,
    description: DECK.seo.description,
    brand: { "@type": "Brand", name: "SnapshotFame" },
    offers: { "@type": "Offer", price: DECK.price, priceCurrency: "USD", availability: "https://schema.org/InStock" },
  };

  return (
    <>
      <SEO {...DECK.seo} canonical="https://snapshotfame.com/mirror-lotus" jsonLd={jsonLd} />
      <section className="pt-32 pb-16 px-6 lg:px-12" data-testid="page-mirror-lotus">
        <div className="max-w-6xl mx-auto">
          <button onClick={() => navigate("/")} className="flex items-center gap-2 text-[#A89FA8] hover:text-[#F5D48A] text-xs tracking-widest uppercase mb-10" data-testid="back-home">
            <ArrowLeft size={14} /> Home
          </button>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="relative">
              <div className="aura" style={{ width: 500, height: 500, top: "-30px", left: "-30px", opacity: 0.6 }}></div>
              <div className="relative flex justify-center items-center h-[560px]">
                {/* Fanned trio of real cards */}
                <img
                  src={CARDS[17].url}
                  alt="Crown of Light"
                  className="absolute w-56 sm:w-60 rounded-lg shadow-2xl"
                  style={{ transform: "rotate(-22deg) translate(-130px, 20px)", zIndex: 1, boxShadow: "0 20px 50px rgba(0,0,0,0.7)" }}
                />
                <img
                  src={CARDS[8].url}
                  alt="The Key"
                  className="absolute w-56 sm:w-60 rounded-lg shadow-2xl"
                  style={{ transform: "rotate(22deg) translate(130px, 20px)", zIndex: 1, boxShadow: "0 20px 50px rgba(0,0,0,0.7)" }}
                />
                <img
                  src={CARDS[20].url}
                  alt="Balance"
                  className="relative w-64 sm:w-72 rounded-lg shadow-2xl float"
                  style={{ zIndex: 2, boxShadow: "0 30px 70px rgba(245, 212, 138, 0.35)" }}
                  data-testid="lotus-image"
                />
              </div>
            </div>
            <div>
              <div className="section-label" data-testid="lotus-label">Mirror Lotus</div>
              <h1 className="display-serif text-5xl sm:text-6xl mb-4" data-testid="lotus-h1">{DECK.seo.h1}</h1>
              <p className="serif italic text-2xl text-[#F5D48A] mb-8">{DECK.seo.subheading}</p>
              {DECK.longDesc.map((p, i) => (
                <p key={i} className="text-[#F2EFE8]/75 text-lg leading-relaxed mb-5" data-testid={`lotus-p-${i}`}>{p}</p>
              ))}

              <ul className="space-y-3 mb-8 mt-6">
                {DECK.includes.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#F2EFE8]/75">
                    <Key size={16} className="text-[#B58B2E] mt-1 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-xs text-[#A89FA8]">USD</span>
                <span className="serif text-5xl gold-shimmer" data-testid="lotus-price">${DECK.price}</span>
              </div>
              <BuyButton product={DECK} onBuy={setCheckoutProduct} />
              <p className="text-[10px] text-[#A89FA8] mt-3 tracking-widest uppercase">Instant Digital Download</p>
            </div>
          </div>
        </div>
      </section>
      <GuidebookPreview />
      <FridayMirror />
      <CardGallery />
      <CheckoutModal product={checkoutProduct} onClose={() => setCheckoutProduct(null)} onSubmit={handleSubmit} />
      <Toast msg={toast} onClose={() => setToast("")} />
    </>
  );
}

// ============ SESSION DETAIL PAGE ============
export function SessionDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [checkoutProduct, setCheckoutProduct] = useState(null);
  const [toast, setToast] = useState("");
  const session = SESSIONS.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (session) fireViewContent(session);
  }, [slug, session]);

  if (!session) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6" data-testid="session-not-found">
        <h1 className="serif text-4xl text-[#F5D48A] mb-4">Not found</h1>
        <button onClick={() => navigate("/")} className="btn-gold">Return Home</button>
      </div>
    );
  }

  const handleSubmit = async (p, form) => {
    try {
      await axios.post(`${API}/orders`, {
        product_id: p.id, product_name: p.name, price: p.price,
        customer_name: form.name, customer_email: form.email,
      });
      fireLeadEvent(p);
      setCheckoutProduct(null);
      setToast(`Thank you, ${form.name.split(" ")[0]}! Dawn will reach out to schedule.`);
    } catch (e) {
      setToast("Something went wrong. Please email divainkhair@gmail.com directly.");
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: session.name,
    description: session.seo.description,
    provider: { "@type": "Person", name: "Dawn Delisle" },
    offers: session.perMinute
      ? { "@type": "Offer", priceSpecification: { "@type": "UnitPriceSpecification", price: 1, priceCurrency: "USD", unitText: "MIN" } }
      : { "@type": "Offer", price: session.price, priceCurrency: "USD" },
  };

  return (
    <>
      <SEO {...session.seo} canonical={`https://snapshotfame.com/sessions/${session.slug}`} jsonLd={jsonLd} />
      <section className="pt-32 pb-16 px-6 lg:px-12" data-testid={`page-session-${session.id}`}>
        <div className={`${session.perMinute ? "max-w-6xl" : "max-w-4xl"} mx-auto`}>
          <button onClick={() => navigate(FOCUS_MODE ? "/" : "/#sessions")} className="flex items-center gap-2 text-[#A89FA8] hover:text-[#F5D48A] text-xs tracking-widest uppercase mb-10" data-testid="back-sessions">
            <ArrowLeft size={14} /> {FOCUS_MODE ? "Back to Home" : "All Sessions"}
          </button>
          <div className="flex items-center gap-3 mb-6">
            <Sparkles size={16} className="text-[#F5D48A]" />
            <span className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8]">{session.duration}</span>
          </div>
          <h1 className="display-serif text-5xl sm:text-6xl md:text-7xl mb-6" data-testid="session-h1">{session.seo.h1}</h1>
          {session.seo.subheading && (
            <p className="serif italic text-2xl mb-10" style={{ color: session.theme }} data-testid="session-subheading">
              {session.seo.subheading}
            </p>
          )}

          <div className={`grid gap-12 mt-16 ${session.perMinute ? "md:grid-cols-5" : "md:grid-cols-3"}`}>
            <div className={`${session.perMinute ? "md:col-span-3" : "md:col-span-2"} space-y-6`}>
              {session.longDesc.map((p, i) => (
                <p key={i} className="text-[#F2EFE8]/80 text-lg leading-relaxed" data-testid={`session-p-${i}`}>{p}</p>
              ))}
              <div className="pt-6 border-t border-[#B58B2E]/20 mt-10">
                <div className="section-label mb-4">What's Included</div>
                <ul className="space-y-3">
                  {session.includes.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-[#F2EFE8]/75">
                      <Key size={16} className="text-[#B58B2E] mt-1 flex-shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className={session.perMinute ? "md:col-span-2" : ""}>
              {session.perMinute ? (
                <ClaritySessionBooking />
              ) : (
                <div className="sf-card jewel-card rounded-sm p-8 sticky top-28" style={{ ["--jewel"]: session.theme }}>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#A89FA8] mb-2">Private Session</div>
                  <div className="flex items-baseline gap-2 mb-6">
                    <span className="text-xs text-[#A89FA8]">USD</span>
                    <span className="serif text-5xl gold-shimmer" data-testid="session-price">${session.price}</span>
                  </div>
                  <BuyButton product={session} onBuy={setCheckoutProduct} label="Book Now" />
                  <p className="text-[10px] text-center text-[#A89FA8] mt-4 tracking-widest uppercase">Dawn reaches out to schedule</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
      <CheckoutModal product={checkoutProduct} onClose={() => setCheckoutProduct(null)} onSubmit={handleSubmit} />
      <Toast msg={toast} onClose={() => setToast("")} />
    </>
  );
}

// ============ ABOUT PAGE ============
export function AboutPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: "Dawn Delisle",
    jobTitle: "Life Clarity Chic",
    description: "More than 40 years of recognizing human patterns through thousands of conversations.",
    url: "https://snapshotfame.com/about",
    image: LOGO_URL,
    sameAs: [
      "https://www.facebook.com/dawndelislepsychic/",
      "https://www.youtube.com/@dawndelisleslife",
    ],
  };
  return (
    <>
      <SEO {...ABOUT_SEO} canonical="https://snapshotfame.com/about" jsonLd={jsonLd} />
      <section className="pt-32 pb-16 px-6 lg:px-12" data-testid="page-about">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div className="relative">
            <div className="aura" style={{ width: 380, height: 380, top: "-40px", left: "-40px", opacity: 0.4 }}></div>
            <div className="relative rounded-sm overflow-hidden border border-[#B58B2E]/30 shadow-2xl">
              <img src={DAWN_PORTRAIT} alt="Dawn Delisle, Life Clarity Chic" className="w-full h-auto object-contain" data-testid="about-full-portrait" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2A1140] via-transparent to-transparent"></div>
            </div>
            {/* Caption under photo */}
            <div className="mt-8 space-y-4 text-center px-2" data-testid="about-photo-caption">
              <p className="serif italic text-2xl text-[#F5D48A] leading-snug">
                Come sit in my chair.
              </p>
              <div className="sf-divider !my-4">
                <span className="line"></span>
                <span className="dot"></span>
                <Key size={14} className="text-[#B58B2E]" />
                <span className="dot"></span>
                <span className="line"></span>
              </div>
              <p className="text-[#F2EFE8]/80 text-base leading-relaxed">
                More than 40 years of recognizing human patterns through thousands of conversations.
              </p>
            </div>
          </div>
          <div>
            <div className="section-label" data-testid="about-full-label">Meet Dawn</div>
            <h1 className="display-serif text-5xl sm:text-6xl md:text-7xl mb-8" data-testid="about-full-h1">
              Dawn Delisle
            </h1>
            <p className="serif italic text-2xl text-[#F5D48A] mb-8">Life Clarity Chic</p>
            <p className="text-[#F2EFE8]/85 text-lg leading-relaxed mb-6">
              We all have our own unique design.
            </p>
            <p className="text-[#F2EFE8]/80 text-lg leading-relaxed mb-6">
              I don't believe you're broken. I believe your Ferrari is already in the garage. Your diamonds are already in your pocket.
            </p>
            <p className="text-[#F2EFE8]/80 text-lg leading-relaxed mb-10">
              Sometimes they've simply been buried beneath fear, relationships, expectations, or old stories.
            </p>
            <div className="border-l-2 border-[#B58B2E] pl-5 mb-10">
              <p className="serif italic text-2xl text-[#F5D48A]">— Dawn Delisle</p>
            </div>
            <div className="flex gap-4">
              <Link to={FOCUS_MODE ? FOCUS_SESSION_PATH : "/#sessions"} className="btn-gold" data-testid="about-cta-sessions">Book a Private Session ⭐</Link>
              {!FOCUS_MODE && <Link to="/#snapshots" className="btn-outline-gold" data-testid="about-cta-snapshots">See Snapshots</Link>}
            </div>
          </div>
        </div>
      </section>
      {!FOCUS_MODE && <FridayMirror />}
    </>
  );
}
